import * as React from "react";
import { useTheme, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MobileStepper from "@mui/material/MobileStepper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";
import useMediaQuery from '@mui/material/useMediaQuery';
import CategoryCard from "../../components/frontView/CategoryCard";
import { useAppSelector } from "../../hooks";
import { CategoryType } from "../../types/types";
import { IMG_BASE_URL } from "../../utils/config";
import { motion } from "framer-motion";

const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const StyledMobileStepper = styled(MobileStepper)(({ theme }) => ({
  "& .MuiMobileStepper-dot": {
    backgroundColor: "#ccc",
  },
  "& .MuiMobileStepper-dotActive": {
    backgroundColor: theme.palette.primary.main,
    width: 20,
    height: 10,
    borderRadius: 5,
    transition: "all .3s",
  },
}));

const groupCategories = (categories: CategoryType[], itemsPerGroup: number) => {
  const groups = [];
  for (let i = 0; i < categories.length; i += itemsPerGroup) {
    groups.push(categories.slice(i, i + itemsPerGroup));
  }
  return groups;
};

const CategorySection: React.FC = () => {
  const { data } = useAppSelector(state => state.frontViewData);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const [activeStep, setActiveStep] = React.useState(0);
  const [categories, setCategories] = React.useState<CategoryType[] | null>(null);
  const [itemsPerGroup, setItemsPerGroup] = React.useState(3);

  React.useEffect(() => {
    if (data && data.data && data.data.activeCategories) {
      setCategories(data.data.activeCategories);
    }
  }, [data]);

  React.useEffect(() => {
    if (isXs) {
      setItemsPerGroup(1);
    } else if (isSm) {
      setItemsPerGroup(2);
    } else if (isMd || isLg) {
      setItemsPerGroup(3);
    }
  }, [isXs, isSm ,isMd, isLg]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepChange = (step: number) => {
    setActiveStep(step);
  };

  const categoryGroups = categories ? groupCategories(categories, itemsPerGroup) : [];

  return (
    <Box sx={{ width: "100%", px: { xs: 3, lg: 10 , xl: 20 } }}>
      <motion.div
        className="box"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2, delay: 0.5 }}
      >
      <Box sx={{ mt: 10, mb: 8 }}>
        <Typography sx={{ fontSize: "22px", display: "inline", fontStyle: "italic" }}>
          Explore by our
        </Typography>
        <Typography
          sx={{
            fontSize: "22px",
            display: "inline",
            fontStyle: "italic",
            color: theme.palette.primary.main,
            fontFamily: "Racing Sans One",
            letterSpacing: "1px",
            ml: 1,
          }}
        >
          job categories
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1 }}>
        <AutoPlaySwipeableViews
          interval={3000}
          axis={theme.direction === "rtl" ? "x-reverse" : "x"}
          index={activeStep}
          onChangeIndex={handleStepChange}
          enableMouseEvents
        >
          {categoryGroups.map((group, index) => (
            <Box key={index} sx={{ display: 'flex', justifyContent: 'center', height: 180, overflow: 'hidden' }}>
              {group.map((category, index) => (
                <CategoryCard
                  key={category.id}
                  index={index + 1}
                  url={`${IMG_BASE_URL}/${category.link}`}
                  categoryName={category.name}
                />
              ))}
            </Box>
          ))}
        </AutoPlaySwipeableViews>
        <StyledMobileStepper
          steps={categoryGroups.length}
          position="static"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === categoryGroups.length - 1}
            >
              Next
              {theme.direction === "rtl" ? (
                <KeyboardArrowLeft />
              ) : (
                <KeyboardArrowRight />
              )}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {theme.direction === "rtl" ? (
                <KeyboardArrowRight />
              ) : (
                <KeyboardArrowLeft />
              )}
              Back
            </Button>
          }
        />
      </Box>
      </motion.div>
    </Box>
  );
};

export default CategorySection;
